import { LINK_LIBRARY_TYPES } from 'constants/linkLibrary';
import type { LinkItem, LinkLibraryLinkRow, LinkLibraryNode, LinkLibraryTreeNode } from 'interface/linkLibraryTree';
/**
 * Define a recursive function to merge nodes
 *
 * @param existingNode
 * @param newNode
 * @param updatedNodeId
 * @returns */

export const mergeNode = (
  existingNode: LinkLibraryNode | null | undefined,
  newNode: LinkLibraryNode,
  updatedNodeId: number | string
): LinkLibraryNode => {
  const getExpandedState = (): boolean => {
    if (!existingNode) {
      return false;
    }

    return existingNode.id === updatedNodeId ? true : Boolean(existingNode.expanded);
  };

  // If the new node has no children, merge it with the existing node
  if (!newNode.children || newNode.children.length === 0) {
    return {
      ...newNode,
      expanded: existingNode ? existingNode.expanded : false,
      isNewNode: !existingNode,
    };
  }

  // Merge child nodes recursively
  const mergedChildren = newNode.children.map((newChild) => {
    const existingChild = existingNode ? existingNode.children.find((node) => node.id === newChild.id) : null;

    if (newChild?.type === 'links') {
      // Function to update links and mark them as new if necessary
      const updateLinks = (oldLinks: LinkItem[] = [], newLinks: LinkItem[] = []): LinkItem[] => {
        const arr = newLinks.map((newObj) => ({
          ...newObj,
          isNewNode: !oldLinks?.some((oldObj) => oldObj.id === newObj.id),
        }));

        return arr;
      };

      const newLinks = updateLinks(existingChild?.links, newChild?.links);

      return {
        ...newChild,
        links: newLinks,
      };
    }

    // Recursively merge child nodes
    const childNode = mergeNode(existingChild, newChild, updatedNodeId);

    return childNode;
  });

  // Return the merged node with updated properties
  return {
    ...newNode,
    expanded: getExpandedState(),
    isNewNode: !existingNode,
    children: mergedChildren,
  };
};

export const getLinkIds = (data: LinkLibraryNode[]): Array<number | string> => {
  let linkIds: Array<number | string> = [];

  data.forEach((item) => {
    if (item.type === 'link' && item.id !== undefined) {
      linkIds.push(item.id);
    }

    if (item.children && Array.isArray(item.children)) {
      linkIds = linkIds.concat(getLinkIds(item.children));
    }
  });

  return linkIds;
};

/**
 * Returns total links when hierarchy data is provided.
 *
 * @param hierarchyData
 * @returns */

export const countLinks = (data: LinkLibraryNode[]): number => {
  const linkIds = getLinkIds(data);
  const filteredIds = linkIds.filter((value) => typeof value === 'number');
  const linkCount = filteredIds.length;

  return linkCount;
};

/**
 * Return formatted request body payload for updating the link library weights.
 *
 * @param data
 * @returns */
export const formattedUpdateWeightBody = (
  data: Array<{ id?: number | string | null; type?: string }>
): { type?: string; data: Array<{ id: number | string; weight: number }> } => {
  const updatedData = data
    .filter((item) => item.id !== undefined && item.id !== null)
    .map((item, index) => {
      return { id: item.id, weight: index + 1 };
    });
  const bodyPayload = {
    type: data?.[0]?.type,
    data: updatedData,
  };

  return bodyPayload;
};

/**
 * Returns the node level of an item using its id and type.
 *
 * @param treeData
 * @param param1
 * @param currentLevel
 * @returns */
export const findNodeLevel = (
  treeData: LinkLibraryNode[],
  { id, type }: { id: number | string; type: string },
  currentLevel = 0
): number => {
  const checkCondition = (node: LinkLibraryNode): boolean => {
    const nodeIdentifier =
      type === LINK_LIBRARY_TYPES.SUBCATEGORY ? `${node.id}-${node.type}` : `${node.categoryId}-${node.type}`;

    return nodeIdentifier === `${id}-${type}`;
  };

  for (const node of treeData) {
    if (checkCondition(node)) {
      return currentLevel;
    }

    if (node.children && node.children.length > 0) {
      const childLevel = findNodeLevel(node.children, { id, type }, currentLevel + 1);
      if (childLevel !== -1) {
        return childLevel;
      }
    }
  }

  return -1;
};

export const isLinkRow = (node: LinkLibraryTreeNode): node is LinkLibraryLinkRow => {
  return node.type === LINK_LIBRARY_TYPES.LINK && typeof node.linkUrl === 'string';
};

export const countRenderedLinks = (nodes: LinkLibraryTreeNode[]): number => {
  return nodes.reduce((total, node) => {
    const childCount = countRenderedLinks(node.children || []);
    const nodeCount = node.type === LINK_LIBRARY_TYPES.LINK && Boolean(node.linkUrl) ? 1 : 0;

    return total + nodeCount + childCount;
  }, 0);
};
